/**
 * Phase 3 V1 — Private A&R Listening Room Panel.
 *
 * Additive UI on top of the existing Live/Beam room. Rendered only
 * when `room_type === "private_ar"`. Reuses the canonical
 * PlayerProvider (NO standalone <audio>) and the existing Phase 2
 * review components (RecommendationPicker, ScoringPanel). Host is
 * authoritative for playback; late joiners hydrate from the server
 * state; reviewers submit reviews through the backend proxy that
 * writes to `spotlight_ar_reviews` exactly like the Phase 2 workspace.
 *
 * WS events consumed (broadcast by backend/private_ar_room_routes.py):
 *   ar_queue_added · ar_queue_removed · ar_queue_reloaded
 *   ar_playback_state · ar_review_progress · ar_authorized_added
 *   ar_authorized_removed · ar_results_revealed
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePlayer } from "@/components/player/PlayerProvider";
import RecommendationPicker from "@/components/judging/RecommendationPicker";
import ScoringPanel from "@/components/contest/ScoringPanel";
import { getProfilePhotoUrl, getCoverUrl } from "@/lib/mediaUrls";

const _fmtMs = (ms) => {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${String(ss).padStart(2, "0")}`;
};

const _buildPayload = (entry) => {
  if (!entry?.submission_id) return null;
  // Resolve via the canonical mediaUrls helper so legacy / bare-filename
  // covers surface the branded RGM placeholder instead of a broken <img>.
  // PlayerProvider will also re-check via /api/track/:id on play().
  const resolvedCover = getCoverUrl({ cover_url: entry.cover_url });
  return {
    submission_id: entry.submission_id,
    trackId: entry.submission_id,
    track_title: entry.title || "Untitled",
    artist_name: entry.artist || "",
    cover_url: resolvedCover,
  };
};

export default function PrivateARPanel({ roomId, me, isHost, arEvent }) {
  const player = usePlayer();

  const [queue, setQueue] = useState([]);
  const [state, setState] = useState({ index: 0, is_playing: false, position_ms: 0, seq: 0 });
  const [spotlightId, setSpotlightId] = useState(null);
  const [authorized, setAuthorized] = useState([]);

  // Phase 2 config + this reviewer's own reviews for the current spotlight.
  const [arConfig, setArConfig] = useState(null);
  const [myReviews, setMyReviews] = useState({});
  const [nomineeUsed, setNomineeUsed] = useState(0);
  const [nomineeCap, setNomineeCap] = useState(2);
  const [isReviewer, setIsReviewer] = useState(false);

  // Review-form working state for the currently-playing track.
  const [rec, setRec] = useState(null);
  const [nominee, setNominee] = useState(false);
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Host-only reviewer progress + results.
  const [progress, setProgress] = useState([]);
  const [results, setResults] = useState(null);
  const [revealed, setRevealed] = useState(false);

  const lastAppliedSeqRef = useRef(-1);

  // ─────────── Initial hydration ───────────
  const loadQueue = useCallback(async () => {
    try {
      const r = await api.get(`/live/rooms/${roomId}/ar/queue`);
      setQueue(r.data?.queue || []);
      setState(r.data?.state || { index: 0, is_playing: false, position_ms: 0, seq: 0 });
      setSpotlightId(r.data?.spotlight_id || null);
    } catch (_e) { /* room may not exist yet */ }
  }, [roomId]);

  const loadAuthorized = useCallback(async () => {
    try {
      const r = await api.get(`/live/rooms/${roomId}/ar/authorized`);
      setAuthorized(r.data?.authorized || []);
    } catch (_e) { /* ignore */ }
  }, [roomId]);

  const loadReviewerContext = useCallback(async (sid) => {
    if (!sid) { setIsReviewer(false); setArConfig(null); return; }
    try {
      const r = await api.get(`/ar/spotlights/${sid}`);
      setArConfig(r.data?.config || null);
      setMyReviews(r.data?.my_reviews || {});
      setNomineeUsed(r.data?.nominee_used || 0);
      setNomineeCap(r.data?.nominee_cap || 2);
      setIsReviewer(true);
    } catch (_e) {
      // 403 means "not a reviewer" — legitimate for host-only observers.
      setIsReviewer(false);
      setArConfig(null);
      setMyReviews({});
    }
  }, []);

  const loadProgress = useCallback(async () => {
    if (!isHost) return;
    try {
      const r = await api.get(`/live/rooms/${roomId}/ar/progress`);
      setProgress(r.data?.progress || []);
    } catch (_e) { /* ignore */ }
  }, [isHost, roomId]);

  const loadResults = useCallback(async () => {
    try {
      const r = await api.get(`/live/rooms/${roomId}/ar/results`);
      setResults(r.data || null);
      setRevealed(!!r.data?.revealed);
    } catch (_e) { /* not revealed yet */ }
  }, [roomId]);

  useEffect(() => {
    loadQueue();
    loadAuthorized();
    loadResults();
  }, [loadQueue, loadAuthorized, loadResults]);

  useEffect(() => { loadReviewerContext(spotlightId); }, [spotlightId, loadReviewerContext]);
  useEffect(() => { loadProgress(); }, [loadProgress]);

  // ─────────── WS event fan-in ───────────
  useEffect(() => {
    if (!arEvent) return;
    const t = arEvent.type;
    if (t === "ar_queue_added" || t === "ar_queue_removed" || t === "ar_queue_reloaded") {
      loadQueue();
    } else if (t === "ar_playback_state") {
      if (arEvent.state) setState(arEvent.state);
    } else if (t === "ar_authorized_added" || t === "ar_authorized_removed") {
      loadAuthorized();
    } else if (t === "ar_review_progress") {
      loadProgress();
    } else if (t === "ar_results_revealed") {
      setRevealed(true);
      loadResults();
    }
  }, [arEvent, loadQueue, loadAuthorized, loadProgress, loadResults]);

  // ─────────── Current entry + review form hydration ───────────
  const curEntry = queue[state.index] || queue[0] || null;
  const curSubId = curEntry?.submission_id || null;
  const curReview = curSubId ? myReviews[curSubId] : null;
  const submitted = !!curReview?.submitted_at;
  const criteria = arConfig?.criteria || [];
  const scale = arConfig?.scale || { min: 1, max: 10 };

  useEffect(() => {
    setRec(curReview?.recommendation || null);
    setNominee(!!curReview?.is_nominee);
    setScores(curReview?.scores || {});
    setNotes(curReview?.notes || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curSubId, curReview?.review_id]);

  // ─────────── Player sync (host-authoritative) ───────────
  useEffect(() => {
    if (!curEntry) return;
    if ((state.seq || 0) === lastAppliedSeqRef.current) return;
    lastAppliedSeqRef.current = state.seq || 0;
    const payload = _buildPayload(curEntry);
    if (!payload) return;
    const trackId = player?.track?.trackId || null;
    const sameTrack = trackId === payload.trackId;
    if (!sameTrack) {
      // Switch track. Autoplay iff host said is_playing.
      player.play(payload, { sourceLabel: "🎧 Private A&R" });
      // Delay pause / seek until source is loaded.
      setTimeout(() => {
        try {
          if (state.position_ms > 500) player.seekTo(state.position_ms / 1000);
          if (!state.is_playing) player.toggle();
        } catch (_e) { /* ignore */ }
      }, 250);
    } else {
      // Same track — reconcile play state + seek if a large jump was sent.
      try {
        if (state.is_playing && !player.isPlaying) player.toggle();
        if (!state.is_playing && player.isPlaying) player.toggle();
        const drift = Math.abs((state.position_ms / 1000) - (player.currentTime || 0));
        if (drift > 2) player.seekTo(state.position_ms / 1000);
      } catch (_e) { /* ignore */ }
    }
  }, [state, curEntry, player]);

  // ─────────── Host controls ───────────
  const pushState = useCallback(async (patch) => {
    try {
      const r = await api.post(`/live/rooms/${roomId}/ar/playback`, patch);
      if (r.data?.state) setState(r.data.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sync playback");
    }
  }, [roomId]);

  const hostTogglePlay = () => {
    if (!isHost || !curEntry) return;
    const nowPlaying = !state.is_playing;
    pushState({
      index: state.index,
      is_playing: nowPlaying,
      position_ms: Math.floor((player.currentTime || 0) * 1000),
    });
  };

  const hostPrev = () => {
    if (!isHost || queue.length === 0) return;
    const nextIdx = Math.max(0, (state.index || 0) - 1);
    if (nextIdx === state.index) return;
    pushState({ index: nextIdx, is_playing: true, position_ms: 0 });
  };

  const hostNext = () => {
    if (!isHost || queue.length === 0) return;
    const nextIdx = Math.min(queue.length - 1, (state.index || 0) + 1);
    if (nextIdx === state.index) return;
    pushState({ index: nextIdx, is_playing: true, position_ms: 0 });
  };

  const hostSeek = (seconds) => {
    if (!isHost) return;
    pushState({
      index: state.index,
      is_playing: state.is_playing,
      position_ms: Math.max(0, Math.floor(seconds * 1000)),
    });
  };

  // ─────────── Review submission ───────────
  const submitReview = async (asDraft) => {
    if (!curSubId) return;
    if (!asDraft && !rec) { toast.error("Pick YES, MAYBE or NO"); return; }
    setSaving(true);
    try {
      await api.post(`/live/rooms/${roomId}/ar/review`, {
        submission_id: curSubId,
        recommendation: rec,
        is_nominee: nominee,
        scores: criteria.length ? scores : {},
        notes,
        submit: !asDraft,
      });
      toast.success(asDraft ? "Draft saved" : "Review submitted");
      await loadReviewerContext(spotlightId);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Could not save review"));
    } finally { setSaving(false); }
  };

  // ─────────── Host: Reveal Results ───────────
  const revealResults = async () => {
    try {
      const r = await api.post(`/live/rooms/${roomId}/ar/reveal`);
      setRevealed(true);
      if (r.data?.results) setResults({ ...r.data.results, revealed: true });
      toast.success("🎉 Results revealed to reviewers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reveal");
    }
  };

  const willAddNominee = nominee && !curReview?.is_nominee;
  const nomineeCounter = `${nomineeUsed}${willAddNominee ? "+1" : ""}/${nomineeCap}`;
  const durSec = player?.duration || 0;
  const posSec = player?.currentTime || 0;

  return (
    <Card
      className="p-4 sm:p-5 bg-gradient-to-br from-[#120820] to-[#0a0510] border-fuchsia-500/30 mb-3"
      data-testid="private-ar-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <p
            className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-black"
            data-testid="private-ar-badge"
          >
            🎧 Private A&R Listening Room
          </p>
          <p className="text-xs text-fuchsia-100/70 mt-0.5">
            Host controls playback · reviewers hear the same track in sync
          </p>
        </div>
        {isHost && spotlightId && (
          <Button
            onClick={revealResults}
            size="sm"
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold"
            data-testid="private-ar-reveal-btn"
            disabled={revealed}
          >
            {revealed ? "✓ Results revealed" : "Reveal Results"}
          </Button>
        )}
      </div>

      {/* Authorized participants strip */}
      <div className="mb-3" data-testid="private-ar-authorized">
        <p className="text-[10px] uppercase tracking-widest text-fuchsia-200/70 font-bold mb-1.5">
          Authorized · {authorized.length}
        </p>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {authorized.length === 0 && (
            <p className="text-xs text-fuchsia-200/50">Only the host is authorized</p>
          )}
          {authorized.map((u) => (
            <div
              key={u.user_id}
              className="flex flex-col items-center gap-0.5 shrink-0 w-14"
              data-testid={`private-ar-authorized-${u.user_id}`}
            >
              <img
                src={getProfilePhotoUrl(u)}
                alt={u.artist_name || u.name || "Reviewer"}
                className="w-10 h-10 rounded-full object-cover ring-1 ring-fuchsia-400/40"
                onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
              />
              <span className="text-[10px] text-white truncate w-full text-center">
                {u.artist_name || u.name || "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Current track card */}
      {curEntry ? (
        <div
          className="rounded-xl bg-black/40 border border-fuchsia-500/20 p-3 mb-3 flex items-center gap-3"
          data-testid="private-ar-current-track"
        >
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/5 shrink-0 flex items-center justify-center">
            {/* Phase 3 V1 cover fix — resolve through the canonical
                mediaUrls helper so legacy / bare-filename records show
                the branded RGM placeholder instead of a broken image. */}
            <img
              src={getCoverUrl({ cover_url: curEntry.cover_url })}
              alt=""
              className="w-full h-full object-cover"
              data-testid="private-ar-cover-img"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-fuchsia-200/70">
              Track {Math.min(state.index + 1, queue.length)} of {queue.length}
            </p>
            <p
              className="text-sm font-bold text-white truncate"
              data-testid="private-ar-track-title"
            >
              {curEntry.title || "Untitled"}
            </p>
            <p
              className="text-[11px] text-fuchsia-200/70 truncate"
              data-testid="private-ar-track-artist"
            >
              {curEntry.artist || "Unknown artist"}
            </p>
          </div>
          <div className="text-right text-[10px] text-fuchsia-200/60 tabular-nums">
            {_fmtMs(state.position_ms || (posSec * 1000))}
            {durSec > 0 && <> / {_fmtMs(durSec * 1000)}</>}
          </div>
        </div>
      ) : (
        <div
          className="rounded-xl bg-black/40 border border-fuchsia-500/20 p-3 mb-3 text-center"
          data-testid="private-ar-empty-queue"
        >
          <p className="text-xs text-fuchsia-200/70">
            {isHost
              ? "No tracks in the queue yet. Bind this room to a Spotlight cycle from the backend."
              : "The host hasn't loaded any tracks yet."}
          </p>
        </div>
      )}

      {/* Host playback controls (or read-only status) */}
      {curEntry && (
        <div className="flex items-center gap-2 mb-3" data-testid="private-ar-controls">
          <Button
            size="sm"
            variant="outline"
            disabled={!isHost || state.index <= 0}
            onClick={hostPrev}
            className="text-fuchsia-100 border-fuchsia-500/40 hover:bg-fuchsia-500/20 disabled:opacity-30"
            data-testid="private-ar-prev-btn"
          >
            ⏮ Prev
          </Button>
          <Button
            size="sm"
            disabled={!isHost}
            onClick={hostTogglePlay}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold disabled:opacity-30"
            data-testid="private-ar-play-btn"
          >
            {state.is_playing ? "⏸ Pause" : "▶ Play"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!isHost || state.index >= queue.length - 1}
            onClick={hostNext}
            className="text-fuchsia-100 border-fuchsia-500/40 hover:bg-fuchsia-500/20 disabled:opacity-30"
            data-testid="private-ar-next-btn"
          >
            Next ⏭
          </Button>
          {!isHost && (
            <span
              className="text-[10px] text-fuchsia-200/60 ml-2"
              data-testid="private-ar-reviewer-status"
            >
              Host is {state.is_playing ? "playing" : "paused"}
            </span>
          )}
        </div>
      )}

      {/* Basic seek slider */}
      {curEntry && durSec > 0 && (
        <div className="mb-3" data-testid="private-ar-seek">
          <input
            type="range"
            min={0}
            max={Math.max(1, Math.floor(durSec))}
            step={1}
            value={Math.min(Math.floor(posSec), Math.floor(durSec))}
            disabled={!isHost}
            onChange={(e) => hostSeek(parseInt(e.target.value, 10))}
            className="w-full accent-fuchsia-500 disabled:opacity-40"
            data-testid="private-ar-seek-slider"
            aria-label="Seek playback"
          />
        </div>
      )}

      {/* Queue list */}
      {queue.length > 0 && (
        <details className="mb-3" data-testid="private-ar-queue-details">
          <summary className="text-[10px] uppercase tracking-widest text-fuchsia-200/70 font-bold cursor-pointer">
            Queue · {queue.length} tracks
          </summary>
          <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
            {queue.map((q, idx) => (
              <li
                key={q.submission_id}
                className={`flex items-center gap-2 text-xs p-1.5 rounded ${idx === state.index ? "bg-fuchsia-500/15 border border-fuchsia-500/40" : "bg-white/5"}`}
                data-testid={`private-ar-queue-item-${q.submission_id}`}
              >
                <span className="text-fuchsia-200/60 w-5 text-right tabular-nums">{idx + 1}</span>
                <span className="text-white truncate flex-1">{q.title}</span>
                <span className="text-fuchsia-200/60 truncate max-w-[8rem]">{q.artist}</span>
                {isHost && idx !== state.index && (
                  <button
                    type="button"
                    onClick={() => pushState({ index: idx, is_playing: true, position_ms: 0 })}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-600/50 hover:bg-fuchsia-500 text-white"
                    data-testid={`private-ar-queue-jump-${q.submission_id}`}
                  >
                    ▶
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Reviewer form */}
      {isReviewer && curEntry && (
        <div
          className="rounded-lg bg-black/30 border border-white/10 p-3 mb-3"
          data-testid="private-ar-review-form"
        >
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-200/80 font-bold mb-2">
            Your review {submitted && <span className="text-emerald-300 ml-1">· Submitted ✓</span>}
          </p>
          <RecommendationPicker
            value={rec}
            onChange={setRec}
            disabled={submitted}
          />
          <label className="mt-3 flex items-center gap-2 text-xs" data-testid="private-ar-nominee-row">
            <input
              type="checkbox"
              checked={nominee}
              onChange={(e) => setNominee(e.target.checked)}
              disabled={submitted || (nomineeUsed >= nomineeCap && !curReview?.is_nominee && !nominee)}
              className="accent-fuchsia-500"
              data-testid="private-ar-nominee-checkbox"
            />
            <span>🌟 Nominate for Top {nomineeCap}
              <span className="text-fuchsia-200/60 ml-1">({nomineeCounter})</span>
            </span>
          </label>
          {criteria.length > 0 ? (
            <div className="mt-3">
              <ScoringPanel
                criteria={criteria}
                scale={scale}
                scores={scores}
                notes={notes}
                disabled={submitted}
                onScoreChange={(cid, v) => setScores((prev) => ({ ...prev, [cid]: v }))}
                onNotesChange={setNotes}
              />
            </div>
          ) : (
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest text-fuchsia-200/70 font-bold block mb-1">
                Private notes (optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
                rows={3}
                disabled={submitted}
                placeholder="Only you and admin will see these notes."
                className="w-full bg-black/40 border border-white/15 rounded p-2 text-xs text-white focus:outline-none focus:border-fuchsia-400/70 disabled:opacity-50"
                data-testid="private-ar-notes-input"
              />
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => submitReview(true)}
              disabled={saving || submitted}
              className="text-fuchsia-100 border-fuchsia-500/40 hover:bg-fuchsia-500/20"
              data-testid="private-ar-save-draft-btn"
            >
              Save draft
            </Button>
            <Button
              size="sm"
              onClick={() => submitReview(false)}
              disabled={saving || submitted}
              className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white font-bold flex-1"
              data-testid="private-ar-submit-btn"
            >
              {submitted ? "Submitted ✓" : "Submit review"}
            </Button>
          </div>
        </div>
      )}

      {/* Host: reviewer completion */}
      {isHost && progress.length > 0 && (
        <div
          className="rounded-lg bg-black/30 border border-white/10 p-3 mb-3"
          data-testid="private-ar-progress"
        >
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-200/80 font-bold mb-2">
            Reviewer completion · {progress.length} assigned
          </p>
          <ul className="space-y-1">
            {progress.map((p) => (
              <li
                key={p.reviewer_user_id}
                className="flex items-center gap-2 text-xs"
                data-testid={`private-ar-progress-${p.reviewer_user_id}`}
              >
                <img
                  src={getProfilePhotoUrl(p.user)}
                  alt=""
                  className="w-6 h-6 rounded-full object-cover"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=80&h=80&fit=crop"; }}
                />
                <span className="text-white truncate flex-1">
                  {p.user?.artist_name || p.user?.name || p.reviewer_user_id.slice(0, 8)}
                </span>
                <span className="text-fuchsia-200/70 tabular-nums">
                  {p.submitted} submitted
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reveal Results — read-only aggregate */}
      {revealed && results?.rows && (
        <div
          className="rounded-lg bg-emerald-500/5 border border-emerald-500/30 p-3"
          data-testid="private-ar-results"
        >
          <p className="text-[10px] uppercase tracking-widest text-emerald-200 font-bold mb-2">
            Results · pool {results.pool_size || results.rows.length}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-emerald-200/70">
                  <th className="text-left py-1">Track</th>
                  <th className="text-right py-1">✓</th>
                  <th className="text-right py-1">?</th>
                  <th className="text-right py-1">✗</th>
                  <th className="text-right py-1">🌟</th>
                  <th className="text-right py-1">Avg</th>
                </tr>
              </thead>
              <tbody>
                {results.rows.map((r) => (
                  <tr
                    key={r.submission_id}
                    className="border-t border-emerald-500/10"
                    data-testid={`private-ar-results-row-${r.submission_id}`}
                  >
                    <td className="py-1 text-white truncate max-w-[10rem]">
                      {r.submission?.track_title || r.submission_id.slice(0, 8)}
                    </td>
                    <td className="py-1 text-right tabular-nums text-emerald-200">{r.yes_count}</td>
                    <td className="py-1 text-right tabular-nums text-amber-200">{r.maybe_count}</td>
                    <td className="py-1 text-right tabular-nums text-rose-200">{r.no_count}</td>
                    <td className="py-1 text-right tabular-nums text-fuchsia-200">{r.nomination_count}</td>
                    <td className="py-1 text-right tabular-nums text-white">{r.avg?.toFixed?.(2) || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}
